// Lightweight UK postcode lookup using api.postcodes.io (free, no key required).
// Returns the resolved area name so the booking flow can confirm "we cover this area"
// and surface a friendly location label in the summary.

import { normalizePostcode } from "@/lib/postcode";

export type PostcodeLookup = {
  postcode: string;       // normalised + space ("SW1A 1AA")
  area: string;           // human-readable e.g. "City of Westminster, London"
  region: string | null;  // e.g. "London"
  country: string;        // "England" | "Scotland" | "Wales" | "Northern Ireland"
};

const cache = new Map<string, PostcodeLookup | null>();

export async function lookupPostcode(raw: string, signal?: AbortSignal): Promise<PostcodeLookup | null> {
  const key = normalizePostcode(raw);
  if (cache.has(key)) return cache.get(key) ?? null;
  try {
    const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(key)}`, { signal });
    if (!res.ok) {
      cache.set(key, null);
      return null;
    }
    const json = (await res.json()) as {
      result: {
        postcode: string;
        admin_district: string | null;
        admin_county: string | null;
        region: string | null;
        country: string;
      };
    };
    const r = json.result;
    const area = [r.admin_district, r.region].filter(Boolean).join(", ") || r.country;
    const value: PostcodeLookup = {
      postcode: r.postcode,
      area,
      region: r.region,
      country: r.country,
    };
    cache.set(key, value);
    return value;
  } catch {
    return null;
  }
}

// Suggest valid completions for partial postcodes (e.g. user typed "SW1A").
export async function autocompletePostcode(prefix: string, signal?: AbortSignal): Promise<string[]> {
  const q = normalizePostcode(prefix);
  if (q.length < 2) return [];
  try {
    const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(q)}/autocomplete`, { signal });
    if (!res.ok) return [];
    const json = (await res.json()) as { result: string[] | null };
    return json.result ?? [];
  } catch {
    return [];
  }
}
